import { useMemo } from 'react';

import STATES from '@constants/configs/states';
import { environmentSelector } from '@selectors/commonSelectors';
import useAppSelector from './useAppSelector';

//Configs
const REPORT_LIFE_EVENT = new Set([STATES.MINNESOTA]);
const SHOW_CANCEL_CONTINUE_BUTTON = new Set([STATES.MINNESOTA]);
const SHOW_EXEMPTIONS_URL = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);
const SHOW_DISENROLL_BOTH_PLANS_ALERT = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);
const SHOW_DISENROLL_OPTIONS = new Set([STATES.NEW_JERSEY]);
const SHOW_DISENROLL_WARNING = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);

const SHOW_VOLUNTARY_DISENROLLMENT = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);

const SHOW_IF_YOU_HAVE_ANY_QUESTIONS = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);

const SHOW_COVERAGE_START_DATE_ALERT = new Set([STATES.NEW_JERSEY, STATES.NEVADA]);

const SHOW_CANCEL_END_OF_PHE = new Set([STATES.NEW_JERSEY, STATES.NEVADA]);

const SHOW_CANCEL_DISENROLLMENT_BUTTON = new Set([STATES.MINNESOTA]);

const SHOW_UNABLE_TO_ENROLL_AGAIN_WARNING = new Set([STATES.MINNESOTA]);

const HIDE_DENTAL_APTC_IF_NO_KIDS = new Set([STATES.IDAHO]);

const SHOW_CHANGE_PLAN_MESSAGE = new Set([STATES.GEORGIA, STATES.NEW_JERSEY]);

const SHOW_APPLICATION_NOT_TO_CONSIDER_DENTAL = new Set([
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.MINNESOTA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);

const HAS_PAY_NOW_WARNING = new Set([STATES.MINNESOTA]);
const HAS_PAY_NOW_FOOTER = new Set([STATES.MINNESOTA]);

const HAS_STATE_SUBSIDY = new Set([STATES.CALIFORNIA, STATES.NEW_JERSEY, STATES.PENNSYLVANIA, STATES.ILLINOIS, STATES.NEW_MEXICO]);

const HAS_PREMIUM_CREDIT = new Set([STATES.CALIFORNIA, STATES.NEW_JERSEY]);

const SHOW_AI_AN_MEMBER_ALERT = new Set([
    STATES.GEORGIA,
    STATES.IDAHO,
    STATES.NEVADA,
    STATES.PENNSYLVANIA,
    STATES.NEW_JERSEY,
    STATES.VIRGINIA,
    STATES.NEW_MEXICO,
    STATES.ILLINOIS,
]);

const SHOW_ADD_MEMBER_WARNING = new Set([STATES.MINNESOTA]);

const HAS_DISENROLL2_WARNING = new Set([STATES.MINNESOTA]);

const HAS_ARE_YOU_SURE = new Set([STATES.MINNESOTA]);

const HAS_WE_HAVE_REVIEWED = new Set([STATES.MINNESOTA]);

const HAS_PRIMARY_CARE_VISIT = new Set([STATES.MINNESOTA]);

const HAS_PEND_STATUS = new Set([STATES.GEORGIA]);

const HAS_NO_ENROLLMENT_BORDER = new Set([STATES.NEVADA, STATES.GEORGIA, STATES.NEW_JERSEY, STATES.VIRGINIA]);

const HIDE_TOGGLE_NO_ENROLLMENT = new Set([STATES.PENNSYLVANIA, STATES.ILLINOIS]);

const HAS_ENROLLMENT_BORDER = new Set([STATES.GEORGIA, STATES.MINNESOTA, STATES.NEW_JERSEY, STATES.IDAHO]);

const NON_STANDARD_GROUP_FAILURE_MODAL = new Set([STATES.MINNESOTA, STATES.ILLINOIS]);

const HAS_BOLD_HEADER = new Set([STATES.MINNESOTA, STATES.IDAHO, STATES.GEORGIA, STATES.NEW_JERSEY]);

const SHOULD_REDIRECT_TO_CUSTOM_GROUPING = new Set([
    STATES.NEVADA,
    STATES.MINNESOTA,
    STATES.IDAHO,
    STATES.PENNSYLVANIA,
    STATES.ILLINOIS,
]);

const HOUSEHOLD_ELIGIGIBLITY_CONDITION_TOOLTIP_STATES = [STATES.PENNSYLVANIA, STATES.ILLINOIS, STATES.NEW_MEXICO];

const HIDE_ELIGIBILITY_OVERVIEW_SUMMARY_HEADER = [STATES.PENNSYLVANIA, STATES.ILLINOIS, STATES.NEW_MEXICO];

const SHOW_STATE_SUBSIDY_2 = new Set([STATES.NEW_MEXICO]);

const SHOW_MAINTAIN_FA_TEXT = new Set([STATES.NEW_JERSEY]);

export const useStateCode = () => {
    const { stateCode } = useAppSelector(environmentSelector);

    const isEnrollmentBorderVisible = useMemo(() => HAS_ENROLLMENT_BORDER.has(stateCode), [stateCode]);

    const hideToggleOnNoEnrollment = useMemo(() => HIDE_TOGGLE_NO_ENROLLMENT.has(stateCode), [stateCode]);

    const isNoEnrollmentBorder = useMemo(() => HAS_NO_ENROLLMENT_BORDER.has(stateCode), [stateCode]);

    const isPendStatusVisible = useMemo(() => HAS_PEND_STATUS.has(stateCode), [stateCode]);

    const isWeHaveReviewedVisible = useMemo(() => HAS_WE_HAVE_REVIEWED.has(stateCode), [stateCode]);

    const isPrimaryCareVisitVisible = useMemo(() => HAS_PRIMARY_CARE_VISIT.has(stateCode), [stateCode]);

    const isStateSubsidyVisible = useMemo(() => HAS_STATE_SUBSIDY.has(stateCode), [stateCode]);

    const isPrimiumCreditVisible = useMemo(() => HAS_PREMIUM_CREDIT.has(stateCode), [stateCode]);

    const isPayNowWarningVisible = useMemo(() => HAS_PAY_NOW_WARNING.has(stateCode), [stateCode]);

    const isDisEnroll2Warning = useMemo(() => HAS_DISENROLL2_WARNING.has(stateCode), [stateCode]);

    const isAreYouSureVisible = useMemo(() => HAS_ARE_YOU_SURE.has(stateCode), [stateCode]);

    const isPayNowFooterVisible = useMemo(() => HAS_PAY_NOW_FOOTER.has(stateCode), [stateCode]);

    const isReportLifeEventVisible = useMemo(() => REPORT_LIFE_EVENT.has(stateCode), [stateCode]);

    const areCancelContinueControlsVisible = useMemo(() => SHOW_CANCEL_CONTINUE_BUTTON.has(stateCode), [stateCode]);

    const isExemptionsUrlVisible = useMemo(() => SHOW_EXEMPTIONS_URL.has(stateCode), [stateCode]);

    const isDisenrollBothPlansAlertVisible = useMemo(() => SHOW_DISENROLL_BOTH_PLANS_ALERT.has(stateCode), [stateCode]);

    const isDisenrollOptionsVisible = useMemo(() => SHOW_DISENROLL_OPTIONS.has(stateCode), [stateCode]);

    const isReportLifeChangeEventVisible = useMemo(() => SHOW_DISENROLL_WARNING.has(stateCode), [stateCode]);

    const isVoluntaryDisenrollmentVisible = useMemo(() => SHOW_VOLUNTARY_DISENROLLMENT.has(stateCode), [stateCode]);

    const isQuestionsTextVisible = useMemo(() => SHOW_IF_YOU_HAVE_ANY_QUESTIONS.has(stateCode), [stateCode]);

    const isCoverageStartDateAlertVisible = useMemo(() => SHOW_COVERAGE_START_DATE_ALERT.has(stateCode), [stateCode]);

    const isCancelEndOfPHEVisible = useMemo(() => SHOW_CANCEL_END_OF_PHE.has(stateCode), [stateCode]);

    const isCancelDisenrollmentButtonVisible = useMemo(
        () => SHOW_CANCEL_DISENROLLMENT_BUTTON.has(stateCode),
        [stateCode],
    );

    const isUnableToEnrollAgainWarningVisible = useMemo(
        () => SHOW_UNABLE_TO_ENROLL_AGAIN_WARNING.has(stateCode),
        [stateCode],
    );

    const isHideDentalAPTCIfNoKids = useMemo(() => HIDE_DENTAL_APTC_IF_NO_KIDS.has(stateCode), [stateCode]);

    const isChangePlanMessageVisible = useMemo(() => SHOW_CHANGE_PLAN_MESSAGE.has(stateCode), [stateCode]);

    const isApplicationNotToConsiderForDentalVisible = useMemo(
        () => SHOW_APPLICATION_NOT_TO_CONSIDER_DENTAL.has(stateCode),
        [stateCode],
    );

    const isAIANMemberAlertVisible = useMemo(() => SHOW_AI_AN_MEMBER_ALERT.has(stateCode), [stateCode]);

    const isAddMemberWarningVisible = useMemo(() => SHOW_ADD_MEMBER_WARNING.has(stateCode), [stateCode]);

    const isNonStandardGroupFailureModalVisible = useMemo(
        () => NON_STANDARD_GROUP_FAILURE_MODAL.has(stateCode),
        [stateCode],
    );

    const isBoldHeader = useMemo(() => HAS_BOLD_HEADER.has(stateCode), [stateCode]);

    const shouldRedirectToCustomGrouping = useMemo(
        () => SHOULD_REDIRECT_TO_CUSTOM_GROUPING.has(stateCode),
        [stateCode],
    );

    const isHouseholdEligibilityConditionTooltipVisible = useMemo(
        () => HOUSEHOLD_ELIGIGIBLITY_CONDITION_TOOLTIP_STATES.includes(stateCode),
        [stateCode],
    );

    const isEligibilityOverviewSummaryHeaderHidden = useMemo(
        () => HIDE_ELIGIBILITY_OVERVIEW_SUMMARY_HEADER.includes(stateCode),
        [stateCode],
    );

    const isStateSubsidy2Visible = useMemo(() => SHOW_STATE_SUBSIDY_2.has(stateCode), [stateCode]);

    const isEligibilityMaintainTextVisible = useMemo(() => SHOW_MAINTAIN_FA_TEXT.has(stateCode), [stateCode]);

    return {
        isEnrollmentBorderVisible,
        hideToggleOnNoEnrollment,
        isNoEnrollmentBorder,
        isPendStatusVisible,
        isWeHaveReviewedVisible,
        isPrimaryCareVisitVisible,
        isPayNowWarningVisible,
        isPayNowFooterVisible,
        isStateSubsidyVisible,
        isDisEnroll2Warning,
        isAreYouSureVisible,
        isPrimiumCreditVisible,
        isReportLifeEventVisible,
        areCancelContinueControlsVisible,
        isExemptionsUrlVisible,
        isDisenrollBothPlansAlertVisible,
        isDisenrollOptionsVisible,
        isReportLifeChangeEventVisible,
        isVoluntaryDisenrollmentVisible,
        isQuestionsTextVisible,
        isCoverageStartDateAlertVisible,
        isCancelEndOfPHEVisible,
        isCancelDisenrollmentButtonVisible,
        isUnableToEnrollAgainWarningVisible,
        isHideDentalAPTCIfNoKids,
        isChangePlanMessageVisible,
        isApplicationNotToConsiderForDentalVisible,
        isAIANMemberAlertVisible,
        isAddMemberWarningVisible,
        isNonStandardGroupFailureModalVisible,
        isBoldHeader,
        shouldRedirectToCustomGrouping,
        isHouseholdEligibilityConditionTooltipVisible,
        isEligibilityOverviewSummaryHeaderHidden,
        isStateSubsidy2Visible,
        isEligibilityMaintainTextVisible,
    };
};
